import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { getHijriYear, getOperationType, rowStyle } from '../../components/common-constants';
import { hasWritePermission } from '../../../components/Authorization/hasWritePermission';
import { Col, Divider, Empty, Form, Typography, Row, Select, Space, Button, Collapse } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { getFormItems } from '../../components/form/formComponents';
import { fetchData, saveData } from '../../service/dataServices';
import CustomAddNewPageContent from '../../../components/Content/CustomAddNewPageContent';
import { useHistory } from 'react-router';
import Authorized from '../../../components/Authorization/Authorized';
import ImageListUploader from '../../components/image-upload/ImageListUploader';
import moment from 'moment';
import ActivityPage from '../../../pages/dashboard/Activity/ActivityPage';
import UserDropdown from '../../components/form/userDropdown';

const { Option } = Select;
const { Text } = Typography;

const SaveCuratorialForm = () => {
  const PATH = '/api/curatorial-forms';
  const { id } = useParams();
  const operation = getOperationType(id);
  const hasWritePerm = hasWritePermission("curatorialForm");

  const history = useHistory();
  const [formObject] = Form.useForm();
  const [form] = Form.useForm();
  const [mainImageFileList, setMainImageFileList] = useState([]);
  const [imageList, setImageList] = useState([]);
  const [initialData, setInitialData] = useState();
  const [dropdowns, setDropdowns] = useState();
  const [initialOtherDimensions, setInitialOtherDimensions] = useState([]);
  const [objects, setObjects] = useState([]);
  const [refetchChangeHistory, setRefetchChangeHistory] = useState(null);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [objectFilters, setObjectFilters] = useState({
    page: 0,
    size: 20,
  });

  const {
    getMultiselectFormItem,
    getInputFormItem,
    getInputNumberFormItem,
    getDatePickerFormItem,
    getMainImageFormItem,
    getTextAreaFormItem,
    getFormSubmitButton,
    getOtherDimensionsFormItems,
  } = getFormItems(dropdowns, hasWritePerm);

  fetchData('/api/dropdowns', setDropdowns);
  if (id) {
    fetchData(PATH + '/' + id, setInitialData);
  }
  const objectResponse = fetchData('/api/objects', data => setObjects(data.content), objectFilters);
  const saveMutation = saveData(PATH, operation.method);
  const fetchObjects = fetchData('/api/objects/' + selectedObjectId, setSelectedObject, null, false);

  useEffect(() => {
    if (selectedObjectId) {
      fetchObjects.refetch().then(resp => {
        let data = resp.data.data;
        const found = objects.find(o => o.id === data.id);
        if (!found) {
          setObjects(prev => [...prev, { id: data.id, inventoryNumber: data.inventoryNumber }])
        }
      })
    }
  }, [selectedObjectId]);

  useEffect(() => {
    if (selectedObject) {
      setInitialOtherDimensions(selectedObject.otherDimensions || []);
      if (selectedObject.mainImage) {
        setMainImageFileList([selectedObject.mainImage]);
      } else {
        setMainImageFileList([]);
      }

      if (selectedObject.imageList) {

        setImageList(selectedObject.imageList);
      } else {
        setImageList([]);
      }

      formObject.setFieldsValue({
        ...selectedObject,
        date: selectedObject.date ? (selectedObject.date) : null,
        hijri: selectedObject.date ? getHijriYear(selectedObject.date) : null,
      });
    }
  }, [selectedObject]);

  useEffect(() => {
    if (initialData && dropdowns) {
      setSelectedObjectId(initialData.objectId);
      form.setFieldsValue({
        ...initialData,
        recordingDate: initialData.recordingDate ? moment(initialData.recordingDate) : null,
      });
    }
  }, [initialData, dropdowns]);

  const triggerChangeHistoryRefetch = () => {
    setRefetchChangeHistory(new Date());
  }

  const onSearch = val => {
    if (val.length >= 4) {
      setObjectFilters((prev) => {
        return ({ ...prev, inventoryNumber: val });
      });
    }
  };

  const onClearSelectedObject = () => {
    setSelectedObjectId(null);
    setSelectedObject(null);
    formObject.resetFields();
    setInitialOtherDimensions([]);
    setMainImageFileList([]);
  };

  const onFinish = (values) => {
    const dataToSend = {
      ...values,
      objectId: selectedObjectId,
    };
    if (id) {
      saveMutation.mutate({ dataToSend, id }, { onSuccess: triggerChangeHistoryRefetch });
    } else {
      saveMutation.mutate({ dataToSend }, { onSuccess: triggerChangeHistoryRefetch });
    }
  };

  const viewObjectButton = selectedObjectId &&
    (<Authorized asset="item" operation="read">
      <Button icon={<ArrowRightOutlined />} onClick={() => history.push(`/save-object/${selectedObjectId}`)}>
        View Object
      </Button>
    </Authorized>
    )

  return (
    <CustomAddNewPageContent title={operation.type + ' Curatorial Form'} subTitle="Curatorial Forms" exportButton={viewObjectButton}>
      <Row style={{ marginLeft: 20, marginRight: 20, justifyContent: 'space-between' }}>
        <Space>
          <Select
            showSearch
            placeholder="Find object by inventory number"
            allowClear
            value={selectedObjectId}
            onClear={onClearSelectedObject}
            onSelect={value => setSelectedObjectId(value)}
            onSearch={onSearch}
            filterOption={false}
            loading={objectResponse.isFetching}
            style={{ width: '36vh' }}
          >
            {
              objects.map(option => (
                <Option value={option.id} key={option.id}>{option.inventoryNumber}</Option>
              ))
            }
          </Select>
          {selectedObject && <Text copyable={{ text: selectedObject?.inventoryNumber }}></Text>}
        </Space>
      </Row>
      <Divider />
      <Form
        form={formObject}
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 16 }}
        layout="vertical"
        autoComplete="off"
      >
        <Row style={rowStyle}>
          <Col span={8} style={{ display: 'flex', alignItems: 'center' }}>
            {getMainImageFormItem('mainImage', true, 'objects', mainImageFileList, setMainImageFileList)}
            {mainImageFileList && mainImageFileList.length === 0 && (
              <div>
                <Space direction="vertical">
                  <Empty description={false} />
                </Space>
              </div>
            )}
          </Col>
          <Col span={8}>
            {getInputFormItem('inventoryNumber', 'Inventory Number', false, 'text', true)}
            {getInputFormItem('objectName', 'Object Name', false, 'text', true)}
            {getInputFormItem('objectTitle', 'Object Title', false, 'text', true)}
            {getInputFormItem('author', 'Author', false, 'text', true)}
            {getMultiselectFormItem('material', 'Material', true)}
            {getMultiselectFormItem('technique', 'Technique', true)}
          </Col>
          <Col span={8}>
            {getInputNumberFormItem('length', 'Length', 'cm', true)}
            {getInputNumberFormItem('width', 'Width', 'cm', true)}
            {getInputNumberFormItem('height', 'Height', 'cm', true)}
            {getInputNumberFormItem('weight', 'Weight', 'gr', true)}
            {getOtherDimensionsFormItems(initialOtherDimensions, true)}
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={12}>
            {getMultiselectFormItem('region', 'Region', true)}
            {getInputFormItem('country', 'Country', false, 'text', true)}
            {getInputFormItem('city', 'City', false, 'text', true)}
            {getInputFormItem('inscriptionType', 'Inscription Type', false, 'text', true)}
          </Col>
          <Col span={12}>
            {getInputFormItem('century', 'Century', false, 'text', true)}
            {getDatePickerFormItem('date', 'Date', null, true)}
            {getInputFormItem('hijri', 'Hijri Calendar', false, 'text', true)}
            {getInputFormItem('inscriptionPosition', 'Inscription Position', false, 'text', true)}
          </Col>
        </Row>
      </Form>
      <Divider />
      <Form
        form={form}
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 16 }}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Row style={rowStyle}>
          <Col span={12}>
            {getInputFormItem('inscriptionDetail', 'Inscription Mark / Detail')}
            {getInputFormItem('language', 'Language / Alphabet')}
            {getInputFormItem('translation', 'Translation')}
          </Col>
          <Col span={12}>
            {getTextAreaFormItem('objectLabel', 'Object Label / Caption', null, false, 1600, 9)}
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={24}>
            {getTextAreaFormItem('background', 'Background / Context', { span: 22 }, false, null, 12)}
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          <Col span={12}>
            {getInputFormItem('keywords', 'Keywords')}
            {getInputFormItem('bibliography', 'Bibliography')}
          </Col>
          <Col span={12}>
            {getInputFormItem('relatedObjects', 'Related Objects')}
            {getInputFormItem('videography', 'Videography')}
          </Col>
        </Row>

        <Divider />
        <Row style={rowStyle}>
          <ImageListUploader
            disabledAdd={true}
            storageFolder="objects"
            maxImageSize={20}
            fileList={imageList}
            setFileList={setImageList}
          />
        </Row>

        <Divider />

        <Row style={rowStyle}>
          <Col span={12}>
            {getDatePickerFormItem('recordingDate', 'Recording Date')}
          </Col>
          <Col span={12}>
            {getInputFormItem('recordedBy', 'Recorded By (To Be Deleted)')}
          </Col>
        </Row>
        <Row style={rowStyle}>
          <Col span={12}>
            <UserDropdown label='Recorded By' fieldName='userId' hasWritePermission={hasWritePerm} />
          </Col>
        </Row>
        <Divider />
        {getFormSubmitButton(!selectedObject)}
      </Form>

      <Divider />
      {id && <Collapse
        bordered={false}
        items={[
          {
            key: 'change-list',
            label: 'Change History',
            forceRender: true,
            children: (
              <ActivityPage
                tableName='curatorial_form'
                entityId={id}
                fetchDataPath='/api/audit/logs/entity'
                refetchTrigger={refetchChangeHistory}
              />
            ),
          }]}
      />}

    </CustomAddNewPageContent>
  );
};

export default SaveCuratorialForm;
